/**
 * DesignPage — Galerie live du Design System GS-Apps v2.1.1
 * Source de vérité visuelle : packages/ui/src/tokens.ts
 * Zéro appel API — affichage statique des tokens.
 */
import { useState, type ReactNode } from 'react';
import { PageHeader } from '@gs/ui';
import {
  GS_COLORS,
  GS_TYPOGRAPHY,
  GS_COMPONENTS,
  GS_SHADOWS,
  GS_RADIUS,
  GS_SPACING,
  GS_DESIGN_SYSTEM_VERSION,
} from '@gs/ui';
import { Plus, RefreshCw, Trash2, Palette } from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────
type Tab = 'couleurs' | 'typographie' | 'composants' | 'espacements';

// ─── Helpers ────────────────────────────────────────────────────

/** Swatch de couleur avec son nom de token et sa valeur hex */
function ColorSwatch({ name, hex }: { name: string; hex: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    void navigator.clipboard.writeText(hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="group flex flex-col items-start gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900 hover:shadow-xs transition-shadow text-left w-full touch-manipulation"
      title={`Copier ${hex}`}
    >
      <div
        className="w-full h-10 rounded-lg ring-1 ring-black/5"
        style={{ backgroundColor: hex }}
      />
      <span className="text-meta font-mono text-slate-600 dark:text-slate-400 truncate w-full">
        {copied ? '✓ Copié !' : hex}
      </span>
      <span className="text-chip text-slate-400 dark:text-slate-500">{name}</span>
    </button>
  );
}

/** Section avec titre */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-4">
      <h3 className="text-body font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-100 dark:border-slate-800 pb-2">
        {title}
      </h3>
      {children}
    </div>
  );
}

// ─── Onglet Couleurs ─────────────────────────────────────────────
function OngletCouleurs() {
  const palettes: [string, Record<string, string>][] = [
    // Hex affiché lu depuis le token (DS-011 : jamais de hex de marque hardcodé).
    [`brand (Rouge GS ${GS_COLORS.orange[500]})`, GS_COLORS.orange as Record<string, string>],
    ['slate', GS_COLORS.slate as Record<string, string>],
    ['blue', GS_COLORS.blue as Record<string, string>],
    ['green', GS_COLORS.green as Record<string, string>],
    ['red', GS_COLORS.red as Record<string, string>],
    ['amber', GS_COLORS.amber as Record<string, string>],
    ['violet', GS_COLORS.violet as Record<string, string>],
  ];

  return (
    <div className="space-y-8">
      {palettes.map(([name, scale]) => (
        <Section key={name} title={name}>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-2">
            {Object.entries(scale).map(([shade, hex]) => (
              <ColorSwatch key={shade} name={shade} hex={hex} />
            ))}
          </div>
        </Section>
      ))}

      {/* Couleurs sémantiques */}
      <Section title="Sémantiques — usage">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { label: 'navActiveBg', value: 'rgba(217,34,49,0.08)' },
            { label: 'statusOk bg', value: GS_COLORS.green[100] },
            { label: 'statusLate bg', value: GS_COLORS.red[100] },
            { label: 'statusNever bg', value: GS_COLORS.amber[100] },
            { label: 'roleAdmin bg', value: GS_COLORS.violet[100] },
            { label: 'roleGestionnaire bg', value: GS_COLORS.blue[100] },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900">
              <div className="h-8 w-8 rounded-lg ring-1 ring-black/5 shrink-0" style={{ backgroundColor: value }} />
              <span className="text-meta text-slate-600 dark:text-slate-400 font-mono">{label}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ─── Onglet Typographie ──────────────────────────────────────────
function OngletTypographie() {
  const hierarchy = GS_TYPOGRAPHY.hierarchy;

  return (
    <div className="space-y-8">
      <Section title="Hiérarchie complète — Option B (immuable v2.0.0)">
        <div className="space-y-3">
          {(Object.entries(hierarchy) as [string, string][]).map(([key, classes]) => (
            <div
              key={key}
              className="flex items-start gap-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4"
            >
              <div className="w-40 shrink-0">
                <span className="text-meta font-mono text-brand-600 dark:text-brand-400">{key}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className={classes}>
                  {key === 'pageTitle' ? 'Titre de page' :
                   key === 'pageSubtitle' ? 'Sous-titre descriptif' :
                   key === 'kpiValue' ? '1 312' :
                   key === 'kpiLabel' ? 'Véhicules synchronisés' :
                   key === 'sectionTitle' ? 'Section principale' :
                   key === 'cardTitle' ? 'Titre de carte' :
                   key === 'sectionLabel' ? 'Catégorie' :
                   key === 'fieldLabel' ? 'Champ de formulaire' :
                   key === 'fieldHint' ? 'Information complémentaire' :
                   key === 'body' ? 'Corps de texte standard — lisible et confortable.' :
                   key === 'secondary' ? 'Texte secondaire plus discret.' :
                   key === 'meta' ? 'Métadonnée, date, id' :
                   key === 'tableHeader' ? 'EN-TÊTE TABLEAU' :
                   key === 'tableCell' ? 'Cellule de données' :
                   key === 'tableNumber' ? '1 924 444' :
                   key === 'tableEmpty' ? '—' :
                   key === 'badge' ? 'Badge' :
                   key === 'sidebarItem' ? 'Élément sidebar' :
                   key === 'sidebarSection' ? 'Section navigation' :
                   key === 'tooltip' ? 'Infobulle' :
                   key === 'badgeLabel' ? 'Déprécié' :
                   key}
                </p>
              </div>
              <div className="shrink-0">
                <code className="text-chip text-slate-400 dark:text-slate-500 font-mono leading-relaxed max-w-xs block text-right">
                  {classes.split(' ').slice(0, 4).join(' ')}
                  {classes.split(' ').length > 4 ? '…' : ''}
                </code>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Échelle de taille">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {(Object.entries(GS_TYPOGRAPHY.fontSize) as [string, string][]).map(([key, val]) => (
            <div key={key} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
              <span className="block text-slate-700 dark:text-slate-200" style={{ fontSize: val }}>Aa</span>
              <span className="text-meta font-mono text-slate-500 mt-1 block">{key} — {val}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ─── Onglet Composants ───────────────────────────────────────────
function OngletComposants() {
  return (
    <div className="space-y-8">

      {/* Boutons */}
      <Section title="Boutons">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 space-y-4">
          <div className="flex flex-wrap gap-3">
            <button type="button" className={GS_COMPONENTS.btnAction}>
              btnAction (standard)
            </button>
            <button type="button" className={GS_COMPONENTS.btnSecondary}>
              btnSecondary
            </button>
            <button type="button" className={GS_COMPONENTS.btnGhost}>
              btnGhost
            </button>
            <button type="button" className={GS_COMPONENTS.btnDanger}>
              btnDanger
            </button>
            <button type="button" className={GS_COMPONENTS.btnAction} disabled>
              Désactivé
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" className={`${GS_COMPONENTS.btnAction} flex items-center gap-2 touch-manipulation`}>
              <Plus size={14} /> Créer
            </button>
            <button type="button" className={`${GS_COMPONENTS.btnAction} flex items-center gap-2 touch-manipulation`}>
              <RefreshCw size={14} /> Synchroniser
            </button>
            <button type="button" className={`${GS_COMPONENTS.btnDanger} flex items-center gap-2 touch-manipulation`}>
              <Trash2 size={14} /> Supprimer
            </button>
          </div>
          <p className="text-meta text-slate-400 dark:text-slate-500 font-mono">
            btnPrimary / btnPrimaryMobile → DEPRECATED — utiliser btnAction / btnActionMobile
          </p>
        </div>
      </Section>

      {/* Cards */}
      <Section title="Cards">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className={`${GS_COMPONENTS.card} dark:bg-slate-900 dark:border-slate-700 p-4`}>
            <p className={GS_TYPOGRAPHY.hierarchy.cardTitle}>card standard</p>
            <p className={`${GS_TYPOGRAPHY.hierarchy.secondary} mt-1`}>rounded-xl border shadow-xs</p>
          </div>
          <div className={`${GS_COMPONENTS.cardLg} dark:bg-slate-900 dark:border-slate-700 p-4`}>
            <p className={GS_TYPOGRAPHY.hierarchy.cardTitle}>cardLg</p>
            <p className={`${GS_TYPOGRAPHY.hierarchy.secondary} mt-1`}>rounded-2xl border-slate-100</p>
          </div>
          {(['ok', 'retard', 'jamais'] as const).map((status) => (
            <div key={status} className={`${GS_COMPONENTS.cardTerrain(status)} dark:bg-slate-900 p-4`}>
              <p className={GS_TYPOGRAPHY.hierarchy.cardTitle}>cardTerrain</p>
              <p className={`${GS_TYPOGRAPHY.hierarchy.secondary} mt-1`}>statut : {status}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Inputs */}
      <Section title="Inputs">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 space-y-4 max-w-md">
          <div className="space-y-1">
            <label className={GS_TYPOGRAPHY.hierarchy.fieldLabel}>Champ standard</label>
            <input className={`${GS_COMPONENTS.input} dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100`} placeholder="Placeholder…" />
            <p className={GS_TYPOGRAPHY.hierarchy.fieldHint}>Focus : ring brand-500/20</p>
          </div>
          <div className="space-y-1">
            <label className={GS_TYPOGRAPHY.hierarchy.fieldLabel}>Recherche</label>
            <input className={`${GS_COMPONENTS.inputSearch} dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100`} placeholder="Rechercher…" />
          </div>
        </div>
      </Section>

      {/* Contrôles quantité */}
      <Section title="Contrôles saisie terrain">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6">
          <div className="flex items-center gap-2 w-32">
            <button type="button" className={GS_COMPONENTS.qtyMinus}>−</button>
            <input className={`${GS_COMPONENTS.inputQty} dark:bg-slate-800 dark:border-slate-600`} defaultValue="5" />
            <button type="button" className={GS_COMPONENTS.qtyPlus}>+</button>
          </div>
          <p className={`${GS_TYPOGRAPHY.hierarchy.fieldHint} mt-3`}>qtyMinus + inputQty + qtyPlus — 32px, rounded-lg</p>
        </div>
      </Section>

      {/* Icônes containers */}
      <Section title="Icônes containers">
        <div className="flex flex-wrap gap-4">
          {(['brand', 'blue', 'green', 'red', 'violet'] as const).map((color) => (
            <div key={color} className="flex flex-col items-center gap-2">
              <div className={GS_COMPONENTS.iconContainer(color)}>
                <Palette size={20} className={GS_COMPONENTS.iconColor(color)} />
              </div>
              <span className="text-meta text-slate-500 font-mono">{color}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Ombres & radius */}
      <Section title="Ombres & Radius">
        <div className="grid sm:grid-cols-4 gap-4">
          {(Object.entries(GS_SHADOWS) as [string, string][]).map(([key, cls]) => (
            <div key={key} className={`bg-white dark:bg-slate-900 rounded-xl p-4 ${cls}`}>
              <span className="text-meta font-mono text-slate-500">GS_SHADOWS.{key}</span>
              <p className="text-meta text-slate-400 mt-1">{cls}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-5 gap-4 mt-4">
          {(Object.entries(GS_RADIUS) as [string, string][]).map(([key, cls]) => (
            <div key={key} className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xs p-4 ${cls}`}>
              <span className="text-meta font-mono text-slate-500">GS_RADIUS.{key}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ─── Onglet Espacements ──────────────────────────────────────────
function OngletEspacements() {
  return (
    <div className="space-y-8">
      <Section title="GS_SPACING — Espacements standards">
        <div className="grid sm:grid-cols-2 gap-3">
          {(Object.entries(GS_SPACING) as [string, string][]).map(([key, val]) => (
            <div key={key} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 flex items-center gap-4">
              <div className="bg-brand-100 dark:bg-brand-950/30 rounded-sm" style={{ width: 24, height: 24 }} />
              <div>
                <span className="text-meta font-semibold text-slate-700 dark:text-slate-200">{key}</span>
                <code className="block text-meta font-mono text-slate-400 dark:text-slate-500">{val}</code>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ─── Page principale ─────────────────────────────────────────────
export default function DesignPage() {
  const [tab, setTab] = useState<Tab>('couleurs');

  const tabs: { key: Tab; label: string }[] = [
    { key: 'couleurs', label: 'Couleurs' },
    { key: 'typographie', label: 'Typographie' },
    { key: 'composants', label: 'Composants' },
    { key: 'espacements', label: 'Espacements' },
  ];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Design System"
        description={`GS-Apps DS v${GS_DESIGN_SYSTEM_VERSION.version} — ${GS_DESIGN_SYSTEM_VERSION.date} · Tokens live depuis packages/ui/src/tokens.ts`}
      />

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 w-fit">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`px-4 py-1.5 rounded-lg text-field font-medium transition-colors touch-manipulation ${
              tab === key
                ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-xs'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {tab === 'couleurs'     && <OngletCouleurs />}
      {tab === 'typographie'  && <OngletTypographie />}
      {tab === 'composants'   && <OngletComposants />}
      {tab === 'espacements'  && <OngletEspacements />}
    </div>
  );
}
